// Сессия и вход (§3 задания Р-09).
//
// Ядро хранит три вещи: токен сессии, идентификатор устройства и ключ устройства. Токен уходит в
// защищённое хранилище платформы (`SecretStore`), приватный ключ не покидает хранилище ключей вовсе —
// наружу из него выходит только подпись.

import { createClient } from "@connectrpc/connect";
import type { Transport } from "@connectrpc/connect";
import { AuthService } from "@yofis/proto/yofis/v1/auth_pb";
import { NodeService } from "@yofis/proto/yofis/v1/node_pb";
import type { NodeInfo } from "@yofis/proto/yofis/v1/node_pb";
import type { Session as SessionMessage } from "@yofis/proto/yofis/v1/auth_pb";
import type { DeviceRegistration } from "@yofis/proto/yofis/v1/common_pb";
import { LinkService } from "@yofis/proto/yofis/v1/link_pb";
import { EventService } from "@yofis/proto/yofis/v1/event_pb";
import type { Event } from "@yofis/proto/yofis/v1/event_pb";
import type { Chat } from "@yofis/proto/yofis/v1/chat_pb";
import type { Room } from "@yofis/proto/yofis/v1/call_pb";
import type { LinkPreview } from "@yofis/proto/yofis/v1/link_pb";
import { KeyAlgorithm as ProtoKeyAlgorithm, Platform } from "@yofis/proto/yofis/v1/common_pb";

import { createDeviceKey, deviceKeyFromPair } from "./keys.ts";
import type { WebCryptoSigner } from "./keys.ts";
import type { SecretStore } from "./platform.ts";
import type { KeyAlgorithm } from "./signing.ts";
import type { AuthSource, AuthState } from "./transport.ts";

/** Хранилище ключа устройства. В браузере — IndexedDB (ключ кладётся как `CryptoKey`, непереносимым). */
export interface DeviceKeyStore {
  load(): Promise<{ algorithm: KeyAlgorithm; pair: CryptoKeyPair } | undefined>;
  save(algorithm: KeyAlgorithm, pair: CryptoKeyPair): Promise<void>;
  clear(): Promise<void>;
}

/** Хранилище ключа в памяти: для тестов и для процессов, которые не переживают перезапуск. */
export class MemoryDeviceKeyStore implements DeviceKeyStore {
  #saved: { algorithm: KeyAlgorithm; pair: CryptoKeyPair } | undefined;

  load(): Promise<{ algorithm: KeyAlgorithm; pair: CryptoKeyPair } | undefined> {
    return Promise.resolve(this.#saved);
  }

  save(algorithm: KeyAlgorithm, pair: CryptoKeyPair): Promise<void> {
    this.#saved = { algorithm, pair };
    return Promise.resolve();
  }

  clear(): Promise<void> {
    this.#saved = undefined;
    return Promise.resolve();
  }
}

/** Как называться серверу при регистрации устройства. */
export interface DeviceDescription {
  name: string;
  platform: Platform;
  appVersion: string;
}

/** Открытая сессия: то, что ядро отдаёт наверх после входа. */
export interface SessionState {
  address: string;
  displayName: string;
  deviceId: string;
  expiresAt: Date | undefined;
  node: NodeInfo | undefined;
}

const tokenKey = "yofis.session.token";
const deviceKey = "yofis.session.device";

/** Управление сессией: вход, восстановление после перезапуска, выход. */
export class Session implements AuthSource {
  readonly #transport: Transport;
  readonly #secrets: SecretStore;
  readonly #keys: DeviceKeyStore;
  readonly #device: DeviceDescription;
  #state: AuthState = {};
  #session: SessionState | undefined;

  constructor(options: {
    transport: Transport;
    secrets: SecretStore;
    keys: DeviceKeyStore;
    device: DeviceDescription;
  }) {
    this.#transport = options.transport;
    this.#secrets = options.secrets;
    this.#keys = options.keys;
    this.#device = options.device;
  }

  /** Текущее состояние для транспорта: он спрашивает перед каждым запросом. */
  current(): AuthState {
    return this.#state;
  }

  /** Открытая сессия или `undefined`, если входа ещё не было. */
  get state(): SessionState | undefined {
    return this.#session;
  }

  /**
   * Алгоритм ключа этого устройства. Нужен наружу, потому что выбор запасного алгоритма не должен быть
   * молчаливым: Ed25519 закреплён намеренно (ADR-031), и если среда его не дала, это надо показать, а
   * не узнать через год из чужого отчёта.
   */
  get algorithm(): KeyAlgorithm | undefined {
    return this.#state.signer?.algorithm;
  }

  /** Сведения об узле до входа: какие способы входа есть и что узел умеет. */
  async nodeInfo(): Promise<NodeInfo> {
    const node = createClient(NodeService, this.#transport);
    const res = await node.getNodeInfo({});
    if (res.node === undefined) {
      throw new Error("узел не вернул сведения о себе");
    }
    return res.node;
  }

  /** Вход по паролю с регистрацией устройства. */
  async loginWithPassword(login: string, password: string): Promise<SessionState> {
    const device = await this.#deviceRegistration();
    const auth = createClient(AuthService, this.#transport);
    const res = await auth.loginWithPassword({ login, password, device });
    return this.#accept(res.session);
  }

  /**
   * Регистрация по ссылке-приглашению: первый владелец узла и новые сотрудники.
   *
   * Пароль с 0.15 **необязателен**: двести человек не должны придумывать секрет до первого экрана.
   * Пустая строка означает «без пароля»; задать его можно потом, а можно никогда — вход по ссылке
   * остаётся рабочим способом.
   */
  async registerByInvite(linkToken: string, displayName: string, password = ""): Promise<SessionState> {
    const device = await this.#deviceRegistration();
    const auth = createClient(AuthService, this.#transport);
    const res = await auth.registerByInvite({ linkToken, displayName, password, device });
    return this.#accept(res.session);
  }

  /**
   * Что за ссылкой — до того, как что-то предлагать человеку.
   *
   * Публичный вызов, сессия не нужна: экран обязан сначала спросить, а потом ветвиться. Пока он этого
   * не делал, внешнему участнику встречи предлагали завести учётную запись в чужой компании.
   */
  async resolveLink(token: string): Promise<LinkPreview | undefined> {
    const links = createClient(LinkService, this.#transport);
    const res = await links.resolveLink({ token });
    return res.preview;
  }

  /** Вход существующего сотрудника по ссылке — без пароля (0.15). */
  async loginWithLink(linkToken: string): Promise<SessionState> {
    const device = await this.#deviceRegistration();
    const auth = createClient(AuthService, this.#transport);
    const res = await auth.loginWithLink({ linkToken, device });
    return this.#accept(res.session);
  }

  /**
   * Вход внешнего участника во встречу: только имя, ни пароля, ни учётной записи (0.17).
   *
   * Сессия гостя живёт сроком его ссылки, а не тридцать дней, и прав у неё девять методов — поэтому
   * «запомнить меня» гостю обещать нельзя, и список чатов ему показывать тоже.
   */
  async joinAsGuest(linkToken: string, displayName: string): Promise<SessionState> {
    const device = await this.#deviceRegistration();
    const auth = createClient(AuthService, this.#transport);
    const res = await auth.joinAsGuest({ linkToken, displayName, device });
    return this.#accept(res.session);
  }

  /**
   * Вход по ссылке в чат или встречу — тем же токеном, которым представился.
   *
   * Три исхода не взаимозаменяемы: чат, встреча или ожидание лобби. Подменять ожидание входом нельзя:
   * человек решит, что уже внутри, и будет ждать звука вместо ответа.
   */
  async joinByLink(token: string): Promise<{ chat: Chat | undefined; room: Room | undefined; lobbyPending: boolean }> {
    const links = createClient(LinkService, this.#transport);
    const res = await links.joinByLink({ token });
    return { chat: res.chat, room: res.room, lobbyPending: res.lobbyPending };
  }

  /**
   * Слушает поток событий узла без локальной базы.
   *
   * Нужно гостю: у него нет ни истории, ни списка чатов — заводить ради ожидания в лобби базу и
   * синхронизацию значило бы обещать ему клиент, которого он не получит (девять доступных методов).
   */
  watchEvents(onEvent: (event: Event) => void, signal?: AbortSignal): void {
    const events = createClient(EventService, this.#transport);
    void (async () => {
      try {
        for await (const response of events.subscribe({ cursor: "" }, signal === undefined ? {} : { signal })) {
          if (response.event !== undefined) {
            onEvent(response.event);
          }
        }
      } catch {
        // Обрыв потока для ожидающего в лобби не ошибка приложения: экран сам покажет, что связи нет.
      }
    })();
  }

  /**
   * Восстановление после перезапуска: токен и устройство из хранилища, ключ — из хранилища ключей.
   * Если чего-то не хватает, сессии нет — молча делаем вид, что вошли, нельзя.
   */
  async restore(): Promise<boolean> {
    const token = await this.#secrets.get(tokenKey);
    const deviceId = await this.#secrets.get(deviceKey);
    const saved = await this.#keys.load();
    if (token === undefined || deviceId === undefined || saved === undefined) {
      return false;
    }
    this.#state = { token, deviceId, signer: deviceKeyFromPair(saved.algorithm, saved.pair) };
    return true;
  }

  /** Выход: сервер забывает сессию, мы — токен. Ключ устройства остаётся: устройство то же самое. */
  async logout(): Promise<void> {
    if (this.#state.token !== undefined) {
      const auth = createClient(AuthService, this.#transport);
      await auth.logout({});
    }
    await this.#secrets.delete(tokenKey);
    this.#state = {};
    this.#session = undefined;
  }

  /** Ключ устройства: берём сохранённый, а если его нет — заводим новый. */
  async #signer(): Promise<WebCryptoSigner> {
    const saved = await this.#keys.load();
    if (saved !== undefined) {
      return deviceKeyFromPair(saved.algorithm, saved.pair);
    }
    const created = await createDeviceKey();
    await this.#keys.save(created.algorithm, created.pair);
    return created;
  }

  async #deviceRegistration(): Promise<DeviceRegistration> {
    const signer = await this.#signer();
    this.#state = { ...this.#state, signer };
    return {
      $typeName: "yofis.v1.DeviceRegistration",
      name: this.#device.name,
      platform: this.#device.platform,
      appVersion: this.#device.appVersion,
      signingKey: {
        $typeName: "yofis.v1.PublicKey",
        algorithm:
          signer.algorithm === "ED25519"
            ? ProtoKeyAlgorithm.ED25519
            : ProtoKeyAlgorithm.ECDSA_P256,
        key: await signer.publicKey(),
      },
    };
  }

  async #accept(session: SessionMessage | undefined): Promise<SessionState> {
    if (session === undefined || session.me === undefined) {
      throw new Error("сервер не вернул сессию");
    }
    this.#state = { ...this.#state, token: session.sessionToken, deviceId: session.deviceId };
    await this.#secrets.set(tokenKey, session.sessionToken);
    await this.#secrets.set(deviceKey, session.deviceId);
    this.#session = {
      address: session.me.address,
      displayName: session.me.displayName,
      deviceId: session.deviceId,
      expiresAt: session.expiresAt === undefined ? undefined : new Date(Number(session.expiresAt.seconds) * 1000),
      node: session.node,
    };
    return this.#session;
  }
}
